import React, { useContext, useCallback, useRef, useLayoutEffect, useState } from 'react';
import SkeletonParliamentaryContent from './SkeletonParliamentaryContent';
import Share from 'components/Dashboard/components/Share';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { createRoot } from 'react-dom/client';
import ShareParliamentaryContent from './ShareParliamentaryContent';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import StakeholderHoverLink from 'components/Dashboard/components/StakeholderHoverLink';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import hasNegativeItemFeedback from 'components/Dashboard/helpers/hasNegativeItemFeedback';
import TextSelector from 'components/Common/TextSelector';
import isValidURL from 'components/Dashboard/utilities/isValidURL';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import time from 'utils/time';
import RateUpdatesContentButton from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContentButton';
import ContentPagePriorityBadge from 'components/Dashboard/ItemController/ContentPagePriorityBadge';

const ShareButtonRender = (props) => {
  const { currentNew, shareButtonWidget, embed } = props;
  const { id, hansardData, title, questionFor, canonicalUrl, objectId, objectType, edmSignatories } = currentNew;
  const crecType = currentNew?.contentType === 'CREC' || currentNew?.collectionCode === 'CREC';
  let item = {
    id,
    title: hansardData || edmSignatories || crecType ? title : questionFor,
    objectID: objectId,
    type: objectType,
  };
  const ShareOptions = {
    item,
    id: item.id,
    title: item.title,
    shareButtonWidget,
    itemShare: embed,
  };
  if (canonicalUrl) {
    ShareOptions.link = `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}`;
  }
  return <Share {...ShareOptions} />;
};

const ParliamentaryNewContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { search } = state;

  const { ParliamentaryRecord } = contentResults;
  const { activeSearch } = search;
  const {
    currentNew,
    scrollbarRef,
    shareButtonWidget,
    contributionId,
    contentContainer,
    scrolling,
    setUpdatingContribution,
    hideSummary,
    reportWidget,
    embed,
    isFromPopup,
    notIncludeBranding,
    dashboardUpdate,
    localURL,
    notShowShare,
  } = props;
  const routerLocation = useLocation();
  const location = localURL ?? routerLocation;
  const navigate = useNavigate();
  const params = useParams();
  const { type } = params;

  const [positioningContribution, setPositioningContribution] = useState(() => {
    const hash = location.hash.split('~');
    return hash[0].includes('contribution') || !!contributionId;
  });

  const scottishOfficialReport =
    location.pathname.includes('scottish-official-report') ||
    currentNew?.objectId?.startsWith('CountrySpecificParliamentaryRecord-Scotland');
  const welshOfficialReport =
    location.pathname.includes('welsh-official-report') ||
    currentNew?.objectId?.startsWith('CountrySpecificParliamentaryRecord-Wales');
  const seneddCommitteeTranscriptReport =
    location.pathname.includes('senedd-committee-transcript') ||
    currentNew?.objectId?.startsWith('SeneddCommitteeTranscript');

  const isMobile = useIsMobile();
  const isSmallMobile = useIsMobile(480);
  const crecType = currentNew?.contentType === 'CREC' || currentNew?.collectionCode === 'CREC';

  const { createMarkup } = useCreateMarkup();

  const renderTitle = () => {
    let title;
    if (
      (!!currentNew.titleHighlighted || !!currentNew.questionForHighlighted) &&
      (activeSearch || shareButtonWidget || getUrlParam('search', localURL))
    ) {
      title =
        currentNew.questionForHighlighted && (currentNew.questionFor || currentNew.questionText)
          ? currentNew.questionForHighlighted
          : currentNew.titleHighlighted;
    } else {
      title = currentNew.title;
    }
    return title;
  };

  const adjustmentWelshAnchors = useRef(null);
  adjustmentWelshAnchors.current = (node) => {
    if (type === 'welsh-official-report') {
      let elements = document.querySelectorAll('.contributionText');
      for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        let id = element.getAttribute('id');
        if (id && id.startsWith('A7')) {
          element.classList.add('adjustment-anchor-welsh');
        }
      }
    }
  };

  const contributionContainer = useCallback((node) => {
    if (node !== null) {
      const createShareButtons = () => {
        let elements = document.querySelectorAll('.debate-item-contributiondebateitem');
        if (elements.length > 1) {
          for (let i = 0; i < elements.length; i++) {
            let item = elements[i];
            let children = item.children[0];
            let shareButton = document.createElement('div');
            const el = createRoot(shareButton);
            el.render(
              <ShareParliamentaryContent
                url={`${window.location.origin}${currentNew.canonicalUrl}?utm_${
                  shareButtonWidget ? 'campaign' : 'source'
                }=share${shareButtonWidget ? '-widget' : ''}-button#contribution-${children.dataset.contributionId}`}
                title={children.dataset.shareTitle}
                crecType={crecType}
              />
            );
            item.appendChild(shareButton);
          }
        }
      };
      createShareButtons();
      adjustmentWelshAnchors.current();
    }
  }, []);
  const highlightNextSiblings = (elem, className) => {
    let elems = Array.from(elem.parentElement.children);
    let elemIndex = elems.findIndex((item) => item.id === elem.id);
    let slicedElems = elems.slice(elemIndex + 1);
    let nextElemIndex = slicedElems.findIndex((item, index) => (!!item.id || item.tagName === 'HR') && index !== 0);
    let finalElems = slicedElems.slice(0, nextElemIndex);
    finalElems.forEach((item) => {
      item.classList.add(className);
    });
  };

  const getPosition = ({ item, alreadyMarkedFeedback, dashboardUpdate }) => {
    const reportType = scottishOfficialReport
      ? 'scottish'
      : welshOfficialReport
        ? 'welsh'
        : seneddCommitteeTranscriptReport
          ? 'senedd'
          : type === 'hansard-content'
            ? 'hansard'
            : 'default';
    switch (reportType) {
      case 'senedd':
        return item.offsetTop + (alreadyMarkedFeedback ? 180 : 100) + (dashboardUpdate ? -70 : 0);
      case 'scottish':
      case 'welsh':
        return item.offsetTop + (alreadyMarkedFeedback ? 180 : 100) + (dashboardUpdate ? -70 : 95);
      case 'hansard':
        return item.offsetTop + (dashboardUpdate ? -60 : 180);
      default:
        return item.offsetTop - 60;
    }
  };

  const updateExtract = useRef(null);
  updateExtract.current = () => {
    let hash = location.hash.split('~');
    if ((hash[0].includes('contribution') || !!contributionId) && !scrolling) {
      setPositioningContribution(true);
      // Set this as early as possible (not just before scrollTop) - handleScroll's auto-hash-sync
      // guard reads this flag on the scroll event our own scrollTop() call triggers, and if the
      // state update hasn't committed yet it re-navigates mid-positioning, causing a retrigger loop.
      if (setUpdatingContribution) {
        setUpdatingContribution(true);
      }
      // Rebuild column-indicator markup before swapping in stakeholder links: it rewrites
      // paragraph innerHTML, which would orphan (and freeze mid-render, as plain text) any
      // StakeholderHoverLink React root already mounted inside if run after the swap.
      removeColumns(parliamentaryContentContainerRef.current);
      if (!isMobile) {
        createStakeholdersComponent();
      }
      let className =
        scottishOfficialReport || welshOfficialReport || seneddCommitteeTranscriptReport
          ? 'scottish-welsh-debate-item-contributiondebateitem-highlighted'
          : 'debate-item-contributiondebateitem-highlighted';
      let elements = document.getElementsByClassName(`${className}`);
      if (elements.length) {
        Array.from(elements).forEach((element) => element.classList.remove(className));
      }
      let id = !!contributionId
        ? `contribution-${contributionId}`
        : scottishOfficialReport || welshOfficialReport || seneddCommitteeTranscriptReport || crecType
          ? hash[0].replace('#', '').replace('contribution-', '')
          : hash[0].replace('#', '');

      let idElement = document.getElementById(id);
      if (idElement) {
        let item =
          scottishOfficialReport || welshOfficialReport || seneddCommitteeTranscriptReport
            ? document.getElementById(id)
            : document.getElementById(id).parentElement;

        if (scottishOfficialReport) {
          highlightNextSiblings(idElement, className);
        } else {
          item.classList.add(className);
        }
        //AE: It's neccessary to wait until the ref is updated correctly before reading/scrolling.
        //rAF (instead of setTimeout) keeps the scroll change in the same paint as the highlight,
        //so the browser never paints the un-scrolled position first.
        requestAnimationFrame(() => {
          let position = getPosition({ item, alreadyMarkedFeedback, dashboardUpdate });
          if (scrollbarRef && scrollbarRef.current) {
            scrollbarRef.current.scrollTop(position);
          }
          setPositioningContribution(false);
        });
        setTimeout(() => {
          if (setUpdatingContribution) {
            setUpdatingContribution(false);
          }
        }, 150);
      } else {
        setPositioningContribution(false);
      }
    } else {
      setPositioningContribution(false);
    }
  };

  useLayoutEffect(() => {
    updateExtract.current();
  }, [location.hash]);

  const openStakeholderFromWidget = (e) => {
    if (shareButtonWidget) {
      e.preventDefault();
      window.open(e.target.href);
    }
  };

  const createStakeholdersComponent = () => {
    if (welshOfficialReport) {
      let elementsWithTimestamp = document.querySelectorAll('.memberInfo');
      for (let i = 0; i < elementsWithTimestamp.length; i++) {
        let item = elementsWithTimestamp[i];
        const timeStamp = item.querySelector('.time');
        if (timeStamp) {
          timeStamp.parentNode.removeChild(timeStamp);
        }
      }
    }

    let elements = document.querySelectorAll('.news-content-in a');
    for (let i = 0; i < elements.length; i++) {
      let item = elements[i];
      if (!!item.href && isValidURL(item.href)) {
        let urlItem = new URL(item.href);
        let pathname = urlItem.pathname;
        if (
          (!item.classList?.contains('edm-link') && pathname && pathname.startsWith('/stakeholders')) ||
          pathname.startsWith('/monitor/stakeholders') ||
          pathname.startsWith('/people/stakeholders') ||
          pathname.startsWith('/stakeholder-mapping')
        ) {
          const timeStamp = item.querySelector('.time');
          if (timeStamp) {
            timeStamp.parentNode.removeChild(timeStamp);
          }
          const stakeholderLink = document.createElement('span');
          const el = createRoot(stakeholderLink);
          el.render(
            <StakeholderHoverLink
              contactId={pathname.match(/[0-9]+/)[0]}
              name={item.textContent}
              contentContainer={contentContainer}
              navigate={navigate}
            />
          );
          item.parentNode.replaceChild(stakeholderLink, item);
        }
      }
    }
  };

  const removeColumns = (node) => {
    if (!!node) {
      const elements = node.querySelectorAll('.column-number-indicator');
      for (let i = 0; i < elements.length; i++) {
        const item = elements[i];
        const textNode = item?.nextSibling;
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          const prevElement = item.previousElementSibling;
          if (prevElement && prevElement.tagName === 'P') {
            prevElement.innerHTML += textNode.textContent;
            textNode.parentNode.removeChild(textNode);
          }
        }
      }
    }
  };

  const parliamentaryContentContainerRef = useRef(null);

  useLayoutEffect(() => {
    const container = parliamentaryContentContainerRef.current;

    if (container) {
      if (!isMobile) {
        createStakeholdersComponent();
      }
      removeColumns(container);
    }
  }, [reportWidget, currentNew?.id]);

  const titleSection = useRef(null);

  let { showFixedTitle, titleStyles } = useTitleScrolledStyles({
    contentContainer,
    titleSection,
    leftValue: 0,
  });

  const alreadyMarkedFeedback = hasNegativeItemFeedback(currentNew?.feedback);

  const findRelatedStakeholder = ({ id }) => {
    if (id) {
      const objectToFind = currentNew?.relatedStakeholders ?? ParliamentaryRecord?.contentRelated?.hits?.StakeHolders;
      const item = objectToFind?.find((item) => item?.id === id);
      if (item) {
        const value = ` (${item?.positions?.length > 0 ? item?.positions?.join(', ') : item?.party})`;
        return value;
      }
    }
  };

  return (
    <>
      <div ref={titleSection}>
        <div
          className={`section-title pb-2 ${
            reportWidget ? 'content-title-in-widget' : 'content-title-with-summary'
          } content-border-title`}
        >
          <h3 className='main-title news-title bg-white ' dangerouslySetInnerHTML={createMarkup(renderTitle())} />
          <div className='row mx-0 justify-content-between align-items-center'>
            <ParliamentaryRecordHeadingDate
              currentNew={currentNew}
              reportWidget={reportWidget}
              numberOfContributions={ParliamentaryRecord?.totalContributions?.hits?.length}
            />
            {!notShowShare && (
              <div className='flex-centered'>
                {!notIncludeBranding && (
                  <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
                )}
              </div>
            )}
            {(currentNew.questionFor || currentNew.questionText) &&
              !!currentNew.sourceUrl &&
              isValidURL(currentNew?.sourceUrl) &&
              isSmallMobile && <GoToSource currentNew={currentNew} reportWidget={reportWidget} />}
          </div>

          <div className='my-2'>
            {!reportWidget && <RateUpdatesContentButton currentNew={currentNew} />}
          </div>
        </div>
      </div>

      <ContentSummary content={currentNew} hide={hideSummary} />
      <div className='pb-3 pb-lg-4 pb-lg-0 specific-content-container position-relative'>
        {positioningContribution && (
          <div className='position-absolute w-100 bg-white' style={{ top: 0, left: 0, zIndex: 1 }}>
            <SkeletonParliamentaryContent />
          </div>
        )}
        <div style={{ visibility: positioningContribution ? 'hidden' : 'visible' }}>
          {/* FIXED TITLE */}
          <div
            className={`content-item-scrolled-title main-sticky section-title bg-white parliamentary-scrolled-title ${
              showFixedTitle && !isFromPopup && !reportWidget ? 'item-scrolled-title-visible' : ''
            }`}
            style={titleStyles}
          >
            <div className='d-flex justify-content-between scrolled-title-container'>
              <h3 className='main-title news-title bg-white' dangerouslySetInnerHTML={createMarkup(renderTitle())} />
              {!notShowShare && (
                <>
                  {!notIncludeBranding && (
                    <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} />
                  )}
                </>
              )}
            </div>
          </div>
          {/* END FIXED TITLE */}
          <TextSelector
            currentNew={currentNew}
            scrollbarRef={scrollbarRef}
            scrolling={scrolling}
            reportWidget={reportWidget}
          >
            <div
              className={`py-3 news-content-in parliamentary-content-in context ${
                type === 'hansard-content' ? 'parliamentary-hansard-content' : ''
              } ${type === 'senedd-committee-transcript' ? 'parliamentary-senedd-committee-transcript-content' : ''}${
                type === 'welsh-official-report' ? 'welsh-official-report-content' : ''
              } ${crecType ? 'parliamentary-crec-content' : ''}`}
              ref={parliamentaryContentContainerRef}
            >
              {currentNew?.extract ? (
                <>
                  <div className='debate-item-contributiondebateitem'>
                    <div
                      className='explanatory-container'
                      dangerouslySetInnerHTML={createMarkup(currentNew.extract)}
                      ref={contributionContainer}
                    />
                  </div>
                </>
              ) : (
                <>
                  {currentNew.hansardData && (
                    <div
                      className='explanatory-container'
                      dangerouslySetInnerHTML={createMarkup(currentNew.body)}
                      ref={contributionContainer}
                    />
                  )}
                  {(currentNew.content || currentNew.htmlContent) && (
                    <div className='debate-item-contributiondebateitem'>
                      <div
                        className='explanatory-container'
                        dangerouslySetInnerHTML={createMarkup(currentNew.content ?? currentNew.htmlContent)}
                        ref={contributionContainer}
                      />
                    </div>
                  )}
                  {currentNew?.crectHtmlContent && (
                    <div
                      className='explanatory-container'
                      dangerouslySetInnerHTML={createMarkup(currentNew.crectHtmlContent)}
                      ref={contributionContainer}
                    />
                  )}
                  {currentNew?.edmSignatories && (
                    <>
                      <div className='debate-item-contributiondebateitem'>
                        <h3 className='mb-0'>Primary Sponsor</h3>
                        <p className='mb-3'>
                          {!!currentNew.primarySponsorContactId ? (
                            <Link to={`${currentNew?.primarySponsorUrl}${location.search}`}>
                              {' '}
                              {currentNew.primarySponsorName}
                            </Link>
                          ) : (
                            <span> {currentNew.primarySponsorName}</span>
                          )}
                        </p>

                        <h3 className='mb-3'>Motion text</h3>
                        <div
                          className='explanatory-container'
                          dangerouslySetInnerHTML={createMarkup(currentNew.body)}
                        />

                        {currentNew?.edmSignatories?.length > 0 && (
                          <>
                            <h3 className='mt-4 mb-3'>Signatures ({currentNew?.edmSignatories?.length})</h3>
                            <div className='row mx-0'>
                              {currentNew?.edmSignatories?.map((item, index) => {
                                const { url, signatoryName, party, contactId, imageId } = item;
                                return (
                                  <div
                                    className={`col-lg-8 d-flex px-0 p${index % 2 === 0 ? 'r' : 'l'}-lg-1 mb-3`}
                                    key={`item-${index}-${contactId}`}
                                  >
                                    <Link
                                      to={url}
                                      className='align-items-start d-flex rounded d-block h-100 edm-link border box-shadow-hover p-3 text-main-blue flex-grow-1 mr-3'
                                    >
                                      <div
                                        className={`stakeholder-profile-image member-picture member-${
                                          !!party ? party.toLowerCase() : ''
                                        }-picture`}
                                        style={{
                                          backgroundImage:
                                            imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
                                              ? `url('https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1')`
                                              : `url('${
                                                  import.meta.env.VITE_APP_CDNURL
                                                }/images/20210722-stakeholders-placeholder.png')`,
                                        }}
                                      />
                                      <div className='flex-grow-1 pl-2'>
                                        <p className='title-h5-bold text-main-blue mb-1'>{signatoryName}</p>
                                        {party !== null && <p className='text-main-blue mb-0'>{party}</p>}
                                      </div>
                                    </Link>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  {(currentNew.questionFor || currentNew.questionText) && (
                    <div className='px-4'>
                      <h3>Question</h3>
                      <p className='question-answer-by'>
                        Asked by
                        {!!currentNew.questionByContactId && (
                          <>
                            {notIncludeBranding ? (
                              <span className='mb-0 text-main-blue ml-1'>{currentNew.questionBy}</span>
                            ) : (
                              <Link
                                to={`/stakeholders/${currentNew.questionByContactId}/${normalize(currentNew.questionBy)}${
                                  shareButtonWidget ? '' : location.search
                                }`}
                                onClick={openStakeholderFromWidget}
                              >
                                {' '}
                                {currentNew.questionBy}
                                {reportWidget && <>{findRelatedStakeholder({ id: currentNew?.questionByContactId })}</>}
                              </Link>
                            )}
                          </>
                        )}
                        {!currentNew.questionByContactId && <span> {currentNew.questionBy}</span>}
                      </p>

                      <p dangerouslySetInnerHTML={createMarkup(currentNew.questionText)}></p>
                      {currentNew.questionHasAnswer && (
                        <>
                          <h3>Answer</h3>
                          <p className='question-answer-by'>
                            Answered by
                            {!!currentNew.answerByContactId && (
                              <>
                                {notIncludeBranding ? (
                                  <span className='mb-0 text-main-blue ml-1'>{currentNew.questionBy}</span>
                                ) : (
                                  <Link
                                    to={`/stakeholders/${currentNew.answerByContactId}/${normalize(currentNew.answerBy)}${
                                      shareButtonWidget ? '' : location.search
                                    }`}
                                    onClick={openStakeholderFromWidget}
                                  >
                                    {' '}
                                    {currentNew.answerBy}
                                    {reportWidget && (
                                      <>{findRelatedStakeholder({ id: currentNew?.answerByContactId })}</>
                                    )}
                                  </Link>
                                )}
                              </>
                            )}
                            {!currentNew.answerByContactId && <span> {currentNew.answerBy}</span>}
                            <span className='question-date'> on {time(currentNew.answerDate).formatLongDate()}</span>
                          </p>
                          <div dangerouslySetInnerHTML={createMarkup(currentNew.answerText)}></div>
                        </>
                      )}
                      {!currentNew.questionHasAnswer && (
                        <p className='px-3 py-2 no-answer-box'>Currently awaiting answer</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </TextSelector>
        </div>
      </div>
    </>
  );
};

const ParliamentaryRecordHeadingDate = (props) => {
  const { currentNew, reportWidget, numberOfContributions } = props;
  const isMobile = useIsMobile();
  const isSmallMobile = useIsMobile(480);
  const crecType = currentNew?.contentType === 'CREC' || currentNew?.collectionCode === 'CREC';
  const params = useParams();
  const type = props?.type ?? params?.type;

  const getCountryLabel = (country) => {
    switch (country) {
      case 'Scotland':
        return `Holyrood${isMobile ? '' : ' (Scotland)'}`;
      case 'Wales':
        return `Senedd${isMobile ? '' : ' (Wales)'}`;
      case 'Northern Ireland':
        return `Stormont${isMobile ? '' : ' (NI)'}`;
      default:
        return 'Westminster';
    }
  };

  const itemClass = () => {
    switch (currentNew?.hansardData?.type?.toLowerCase()) {
      case 'commons':
        return 'legislation-commons';
      case 'lords':
        return 'legislation-lords';
      default:
        return 'legislation-grey';
    }
  };

  return (
    <p className='heading-date mb-0'>
      {crecType ? (
        <>
          <span className={`${itemClass()}`}>Congressional Record</span>
          <span className='dot'></span>
          {currentNew.house && (
            <>
              <span className={`${itemClass()}`}>{currentNew.house}</span>
              <span className='dot'></span>
            </>
          )}
          {currentNew.congressionalPagePrefix && currentNew.congressionalPageNumber && (
            <>
              <span className={`${itemClass()}`}>
                {currentNew.congressionalPagePrefix}
                {currentNew.congressionalPageNumber}
                {currentNew.congressionalOrderOnPage > 0 ? `-${currentNew.congressionalOrderOnPage}` : ''}
              </span>
              <span className='dot'></span>
            </>
          )}
          <span className='date'>
            {time(currentNew.dateIssued, { utc: true, skipTimezone: true }).formatLongDate()}
          </span>
        </>
      ) : (
        <>
          {currentNew.hansardData && (
            <>
              <span className={`${itemClass()}`}>
                {currentNew.hansardData.type.charAt(0).toUpperCase() + currentNew.hansardData.type.slice(1)}
              </span>
              <span className='dot'></span>
              {currentNew.type}
              {currentNew.type !== '' && <span className='dot'></span>}
              {time(currentNew.hansardData.date).formatLongDate()}
            </>
          )}
          {(type === 'scottish-official-report' || type === 'welsh-official-report') && (
            <>{time(currentNew.date).formatLongDate()}</>
          )}
          {(currentNew.questionFor || currentNew.questionText) && (
            <>
              Tabled on {time(currentNew.questionDate).formatLongDate()}
              <span className='dot' />
              {getCountryLabel(currentNew?.country)}
              {!!currentNew.sourceUrl && isValidURL(currentNew?.sourceUrl) && !isSmallMobile && (
                <GoToSource dot currentNew={currentNew} reportWidget={reportWidget} />
              )}
            </>
          )}
          {currentNew.tabledOn && (
            <span className='heading-date mb-0'>
              EDM {currentNew.number}: Tabled on {time(currentNew.tabledOn).formatLongDate()} in the{' '}
              {currentNew.session} session
            </span>
          )}
          {(!!currentNew?.hansardData?.href ||
            (!!currentNew.sourceUrl &&
              isValidURL(currentNew?.sourceUrl) &&
              !currentNew.questionFor &&
              !currentNew.questionText)) && (
            <GoToSource dot={true} currentNew={currentNew} reportWidget={reportWidget} />
          )}
        </>
      )}
      <ContentPagePriorityBadge currentNew={currentNew} numberOfContributions={numberOfContributions} />
    </p>
  );
};

const GoToSource = ({ dot, currentNew, reportWidget }) => {
  const isSmallMobile = useIsMobile(480);
  return (
    <>
      {dot && <span className='dot'></span>}
      <a
        className={`legislation-source mb-0 ${isSmallMobile ? 'w-100' : ''}`}
        href={currentNew?.hansardData?.href ?? currentNew?.sourceUrl}
        target='_blank'
        rel='noopener noreferrer'
      >
        {reportWidget ? 'Source' : 'Go to source'}
      </a>
    </>
  );
};

export { ParliamentaryRecordHeadingDate };
export default ParliamentaryNewContent;
